/**
 * @file        transient-connections.js
 *
 *              Centralized management of transient DCP connections used on an ad-hoc basis by the
 *              process, eg those opened by dcp-client Compute API calls.
 *              getTransientConnection will return a connection which will be able to try to send a message.
 *
 *              This code counts on event-emission order in the dcp Connection class -- specifically,
 *              that when the Connection class calls its `emit` even that the event handler is invoked
 *              immediateoy ("synchronously"), rather than on the next pass of the event loop.
 *
 * @author      Wes Garland, wes@distributive.network
 * @date        Nov 2022
 */
'use strict';

const protocol = require('dcp/protocol');
const { assert } = require('dcp/common/dcp-assert');
const { setTimeout, clearTimeout } = require('dcp/common/dcp-timers');

const debugging = require('dcp/debugging').scope('transient-connections');
const tcStateMemos = {};
const defaultTimeout = 10;

/**
 * Factory to create a transient connection for a given service.  A transient connection is always ready
 * to transmit messages, but may not always have an underlying DCP or TCP connection. Connections are
 * created and destroyed based on demand. Messages to the same service are not guaranteed to be
 * delivered on the same connection, unless they are sent in the same pass of the event loop. Messages
 * which cannot be sent are rejected per usual DCP semantics. This usually means that the target is
 * currently unavailable (and retrying immediately is unlikely to succeed).
 * 
 * Only one transient connection per origin+pathname is supported.
 */
function makeTransientConnection(serviceConfig, serviceName, stateMemo, timeout)
{
  var timer;
  const timeoutMs = timeout * 1000;
  
  prepareNewConnection();
  return;

  /**
   * Prepare an instance of Connection for use via getConnection(). 
   */
  function prepareNewConnection()
  {
    stateMemo.preparedDate = new Date();
    stateMemo.connection = new protocol.Connection(serviceConfig);

    const connection = stateMemo.connection;
    connection.unref();

    connection.on('end',          tc$endHandler);
    
    /* Events indicating messages being sent, so reschedule close attempt */
    connection.on('sent',         scheduleCloseAttempt);
    connection.on('recvResponse', scheduleCloseAttempt);
  }

  function finalizeConnection(connection)
  {
    connection.off('end',          tc$endHandler);
    connection.off('sent',         scheduleCloseAttempt);
    connection.off('recvResponse', scheduleCloseAttempt);
  }

  /**
   * Schedule or reschedule the close attempt.
   */
  function scheduleCloseAttempt()
  {
    const nextCloseTimeMs = Date.now() + timeoutMs;
    if (!timer || !(nextCloseTimeMs < stateMemo.closeTimeMs)) /* Previous timer is longer than this one */
    {
      stateMemo.closeTimeMs = nextCloseTimeMs;
      clearTimeout(timer);
      timer = setTimeout(tc$closeIfNotActive, timeoutMs);
      timer.unref();
    }

    debugging() && console.debug('tc - next close attempt in', (stateMemo.closeTimeMs - Date.now()) + 'ms');
  }

  /**
   * Observe the closing of a DCP Connection which is or was a transient connection. API consumers
   * should not be closing these transient connections directly, but if they do, we get another one
   * ready.
   */
  function tc$endHandler()
  {
    const connection = stateMemo.connection;
    debugging() && console.debug(`${serviceName} ${this.dcpsid} closed,`);
    if (this === connection)
      prepareNewConnection();
    finalizeConnection(this);
  }

  /**
   * Timer callback which closes a connection if it's not active.  When this happens, another one
   * is immediate prepared for use.
   */   
  function tc$closeIfNotActive()
  {
    if (!stateMemo.connection.isQuiescent())
      return; /* still active - must have an event-firing race */

    debugging() && console.debug(`${serviceName} not active, closing ${stateMemo.connection.dcpsid}`);
    
    const oldConnection = stateMemo.connection;
    prepareNewConnection();
    finalizeConnection(oldConnection);
    setImmediate(() => oldConnection.close());
  }
}

/**
 * Get a transient Connection for a given service config. The connection may be shared
 * among other passes of the event loop, etc, however the return value of this function
 * MUST be on the same pass of the event loop as when it was acquired; otherwise the
 * behaviour is undefined.
 *
 * @example 
 *   await getTransientConnection(dcpConfig.scheduler.services.jobSubmit).send(message);
 *
 * @param {object} serviceConfig        a dcpConfig fragrament (or similar object) suitable for use as
 *                                      the target parameter of the dcp::Connection() constructor.
 * @param {number} timeout              [otional] the number of seconds to wait after the Connection
 *                                      becomes idle before we close it.
 * @returns instance of Connection. Do not cache the return value of this function!
 */
exports.getTransientConnection = function computeApi$$getTransientConnection(serviceConfig, timeout)
{
  const serviceName = serviceConfig.location.origin + serviceConfig.location.pathname;
  
  if (!tcStateMemos[serviceName])
  {
    tcStateMemos[serviceName] = {
      creationDate: new Date(),
      connection:   undefined,
    };
    makeTransientConnection(serviceConfig, serviceName, tcStateMemos[serviceName], timeout || defaultTimeout);
  }
  
  return tcStateMemos[serviceName].connection;
}

exports.__state = tcStateMemos; /* debug only */
